/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { UserWithDelegation, UserWithStats } from '../../../api/initiative-stats';
import { MessageFormProps, MessageFormField, StatusOption, QuestionStatusStats } from '../types';
import { DestinationCheckboxFields } from './DestinationCheckboxFields';
import { MessageField } from './MessageField';
import { QuestionsFilter } from './QuestionsFilter';
import { SubjectField } from './SubjectField';
import { UserSelectField } from './UserSelectField';
import { QUESTION } from '../../../constants/terminology';

interface BulkUsersMessageFormProps extends MessageFormProps {
  users: UserWithStats[];
}

const getOption = (label: string, icon: string, colour: string) => {
  return (
    <div className='w-100'>
      {icon ? <i style={{ width: '18px' }} className={`fa ${icon} ${colour} mr-2`} /> : null}
      {label}
    </div>
  );
}

const filterOptions: StatusOption[] = [
  {
    value: 'all',
    label: getOption(`All ${QUESTION.SINGULAR} statuses`, 'fa-users-line', 'text-primary'),
  },
  {
    value: 'created',
    label: getOption(`Unanswered ${QUESTION.PLURAL}`, 'fa-user-clock', 'text-muted'),
  },
  {
    value: 'updated',
    label: getOption(`Updated ${QUESTION.PLURAL}`, 'fa-user-edit', 'text-primary'),
  },
  {
    value: 'rejected',
    label: getOption(`Rejected ${QUESTION.PLURAL}`, 'fa-user-times', 'text-danger'),
  },
  {
    value: 'verified',
    label: getOption(`Verified ${QUESTION.PLURAL}`, 'fa-user-check', 'text-success'),
  },
];

export const BulkUsersMessageForm = (props: BulkUsersMessageFormProps) => {
  const { onChange, form, users } = props;
  const [usersState, setUsers] = useState<UserWithDelegation[]>(users);

  const onChangeQuestionsFilter = (value: QuestionStatusStats) => {
    const filteredUsers =
      value === 'all'
        ? users
        : users.filter(({ contributor, verifier }: Partial<UserWithDelegation>) => {
            return (contributor && contributor[value] > 0) || (verifier && verifier[value] > 0);
          });
    const filteredIds = filteredUsers.map(user => user._id);
    setUsers(filteredUsers);
    onChange(MessageFormField.UserIds)(filteredIds);
  };

  return (
    <div className='d-flex flex-column'>
      <div className='row'>
        <div className='col-7'>
          <QuestionsFilter label='Message users with' options={filterOptions} onChange={onChangeQuestionsFilter} />
        </div>
      </div>
      <UserSelectField users={usersState} userIds={form.userIds} onChange={onChange(MessageFormField.UserIds)} />
      <SubjectField subject={form.subject} onChange={onChange(MessageFormField.Subject)} />
      <MessageField message={form.message} onChange={onChange(MessageFormField.Message)} />
      <DestinationCheckboxFields sendByEmail={form.sendByEmail} onChange={onChange(MessageFormField.SendByEmail)} />
    </div>
  );
}
